import { BaseRender2DType, BaseRenderNode2D } from "../../NodeRender2D/BaseRenderNode2D";
import { Color } from "../../maths/Color";
import { Vector2 } from "../../maths/Vector2";
import { Vector4 } from "../../maths/Vector4";
import { ESpineRenderType } from "../SpineSkeleton";
import { SpineShaderInit } from "../material/SpineShaderInit";
import { SpineMeshUtils } from "../mesh/SpineMeshUtils";
import { AnimationRenderProxy } from "./AnimationRenderProxy";
import { SkinRenderUpdate } from "./SkinRenderUpdate";
export class SpineOptimizeRender {
    constructor(spineOptimize) {
        this._skinIndex = 0;
        this.renderProxyMap = new Map();
        this._dynamicMap = new Map;
        this.animatorMap = new Map();
        this.skinRenderArray = [];
        this.boneMat = new Float32Array(spineOptimize.maxBoneNumber * 8);
        spineOptimize.skinAttachArray.forEach((value) => {
            this.skinRenderArray.push(new SkinRenderUpdate(this, value));
        });
        let animators = spineOptimize.animators;
        for (let i = 0, n = animators.length; i < n; i++) {
            let animator = animators[i];
            this.animatorMap.set(animator.name, new AnimationRenderProxy(animator));
        }
        this.currentRender = this.skinRenderArray[this._skinIndex];
    }
    getSpineColor() {
        return this.spineColor;
    }
    destroy() {
        this.skinRenderArray.forEach(skin => skin.destroy());
        this._dynamicMap.forEach(mesh => mesh.destroy());
        this._dynamicMap.clear();
        this._nodeOwner._onMeshChange(null);
    }
    initBake(obj) {
        this.bakeData = obj;
        if (obj) {
            let render = this.renderProxyMap.get(ERenderProxyType.RenderBake) || new RenderBake(this._nodeOwner);
            render.simpleAnimatorTexture = obj.texture2d;
            render._bonesNums = obj.bonesNums;
            render.aniOffsetMap = obj.aniOffsetMap;
            this.renderProxyMap.set(ERenderProxyType.RenderBake, render);
        }
        this.isBake = !!obj;
        if (this._curAnimationName) {
            this._clear();
            this.play(this._curAnimationName);
        }
    }
    changeSkeleton(skeleton) {
        this._skeleton = skeleton;
        this.renderProxyMap.forEach(render => {
            render.changeSkeleton(skeleton);
        });
        skeleton.showSkinByIndex(this._skinIndex);
        this._skeleton.setSlotsToSetupPose();
    }
    init(skeleton, templet, renderNode, state) {
        this._skeleton = skeleton;
        this._nodeOwner = renderNode;
        let scolor = skeleton.color;
        this.spineColor = new Color(scolor.r, scolor.g, scolor.b, scolor.a);
        let color = renderNode._spriteShaderData.getColor(BaseRenderNode2D.BASERENDER2DCOLOR) || new Color();
        color.setValue(scolor.r, scolor.g, scolor.b, scolor.a);
        if (renderNode._renderAlpha !== undefined) {
            color.a *= renderNode._renderAlpha;
        }
        else
            color.a *= renderNode.owner.alpha;
        renderNode._spriteShaderData.setColor(BaseRenderNode2D.BASERENDER2DCOLOR, color);
        this.skinRenderArray.forEach((value) => {
            value.init(skeleton, templet, renderNode);
        });
        this._state = state;
        this.animatorMap.forEach((value, key) => {
            value.state = state;
        });
        let renderOptimize = new RenderOptimize(this._nodeOwner);
        let renderNormal = new RenderNormal(this._nodeOwner);
        this.renderProxyMap.set(ERenderProxyType.RenderNormal, renderNormal);
        this.renderProxyMap.set(ERenderProxyType.RenderOptimize, renderOptimize);
    }
    get renderProxytype() {
        return this._renderProxytype;
    }
    set renderProxytype(value) {
        if (this.isBake && value == ERenderProxyType.RenderOptimize) {
            if (this.bakeData.aniOffsetMap[this._curAnimationName] != undefined) {
                value = ERenderProxyType.RenderBake;
            }
        }
        this.renderProxy = this.renderProxyMap.get(value);
        if (value == ERenderProxyType.RenderNormal) {
            this._nodeOwner._spriteShaderData.removeDefine(SpineShaderInit.SPINE_FAST);
            this._nodeOwner._spriteShaderData.removeDefine(SpineShaderInit.SPINE_RB);
        }
        this._renderProxytype = value;
    }
    beginCache() {
        this._state.apply = this._state.applyCache;
        this._state.getCurrentPlayTime = this._state.getCurrentPlayTimeByCache;
        this._skeleton.updateWorldTransform = this._skeleton.updateWorldTransformCache;
    }
    endCache() {
        this._state.apply = this._state.oldApply;
        this._state.getCurrentPlayTime = this._state.getCurrentPlayTimeOld;
        this._skeleton.updateWorldTransform = this._skeleton.oldUpdateWorldTransform;
    }
    setSkinIndex(index) {
        this._skinIndex = index;
        this.currentRender = this.skinRenderArray[index];
        switch (this.currentRender.skinAttachType) {
            case ESpineRenderType.boneGPU:
                this._nodeOwner._spriteShaderData.addDefine(SpineShaderInit.SPINE_FAST);
                this._nodeOwner._spriteShaderData.removeDefine(SpineShaderInit.SPINE_RB);
                break;
            case ESpineRenderType.rigidBody:
                this._nodeOwner._spriteShaderData.addDefine(SpineShaderInit.SPINE_RB);
                this._nodeOwner._spriteShaderData.removeDefine(SpineShaderInit.SPINE_FAST);
                break;
            case ESpineRenderType.normal:
                this._nodeOwner._spriteShaderData.removeDefine(SpineShaderInit.SPINE_FAST);
                this._nodeOwner._spriteShaderData.removeDefine(SpineShaderInit.SPINE_RB);
                break;
        }
        if (this.currentAnimation) {
            this._clear();
            this.play(this._curAnimationName);
        }
    }
    getDynamicMesh(vertexDeclaration, create = true) {
        let id = vertexDeclaration.id;
        let mesh = this._dynamicMap.get(id);
        if (!mesh && create) {
            mesh = SpineMeshUtils.createMeshDynamic(vertexDeclaration);
            this._dynamicMap.set(id, mesh);
        }
        return mesh;
    }
    _clear() {
        this._nodeOwner.clear();
        this._isRender = false;
    }
    play(animationName) {
        this._curAnimationName = animationName;
        let currentRender = this.currentRender;
        let oldRenderProxy = this.renderProxy;
        let old = this.currentAnimation;
        let oldSkinData = old ? old.currentSKin : null;
        let currentAnimation = this.currentAnimation = this.animatorMap.get(animationName);
        currentAnimation.skinIndex = this._skinIndex;
        let currentSKin = currentAnimation.currentSKin;
        if (old) {
            old.reset();
        }
        if (currentSKin.isNormalRender) {
            this.renderProxytype = ERenderProxyType.RenderNormal;
        }
        else {
            if (currentRender.vertexBones > 4) {
                console.warn(`In FastRender mode - Current skin: ${currentRender.name} has ${currentRender.vertexBones} bones influencing each vertex. This exceeds the recommended limit of 4 bones per vertex.`);
            }
            switch (this.currentRender.skinAttachType) {
                case ESpineRenderType.boneGPU:
                    this._nodeOwner._spriteShaderData.addDefine(SpineShaderInit.SPINE_FAST);
                    this._nodeOwner._spriteShaderData.removeDefine(SpineShaderInit.SPINE_RB);
                    break;
                case ESpineRenderType.rigidBody:
                    this._nodeOwner._spriteShaderData.addDefine(SpineShaderInit.SPINE_RB);
                    this._nodeOwner._spriteShaderData.removeDefine(SpineShaderInit.SPINE_FAST);
                    break;
                case ESpineRenderType.normal:
                    this._nodeOwner._spriteShaderData.removeDefine(SpineShaderInit.SPINE_FAST);
                    this._nodeOwner._spriteShaderData.removeDefine(SpineShaderInit.SPINE_RB);
                    break;
            }
            if (old && oldSkinData.isNormalRender) {
                this._clear();
            }
            if (oldSkinData != currentSKin || !this._nodeOwner._mesh) {
                currentRender.renderUpdate(currentSKin, -1, 0);
            }
            if (this._isRender) {
            }
            else {
                this.renderProxytype = ERenderProxyType.RenderOptimize;
                this._isRender = true;
            }
        }
        if (oldRenderProxy) {
            oldRenderProxy.leave();
        }
        this.renderProxy.change(currentRender, currentAnimation);
        if ((currentAnimation.animator.isCache || this.renderProxytype == ERenderProxyType.RenderBake) && !currentSKin.isNormalRender) {
            this.beginCache();
        }
        else {
            this.endCache();
        }
    }
    render(time) {
        this.renderProxy.render(time, this.boneMat);
    }
}
var ERenderProxyType;
(function (ERenderProxyType) {
    ERenderProxyType[ERenderProxyType["RenderNormal"] = 0] = "RenderNormal";
    ERenderProxyType[ERenderProxyType["RenderOptimize"] = 1] = "RenderOptimize";
    ERenderProxyType[ERenderProxyType["RenderBake"] = 2] = "RenderBake";
})(ERenderProxyType || (ERenderProxyType = {}));
class RenderOptimize {
    constructor(renderNode) {
        this._renderNode = renderNode;
        this.changeSkeleton(renderNode.getSkeleton());
    }
    changeSkeleton(skeleton) {
        this._skeleton = skeleton;
        this.bones = skeleton.bones;
        this.slots = skeleton.slots;
    }
    change(currentRender, currentAnimation) {
        this.skinUpdate = currentRender;
        this.currentAnimation = currentAnimation;
    }
    leave() {
    }
    render(curTime, boneMat) {
        this.currentAnimation.render(this.bones, this.slots, this.skinUpdate, curTime, boneMat, -this._skeleton.x, -this._skeleton.y);
        this._renderNode._spriteShaderData.setBuffer(SpineShaderInit.BONEMAT, boneMat);
    }
}
class RenderNormal {
    constructor(renderNode) {
        this._renderNode = renderNode;
        this.changeSkeleton(renderNode.getSkeleton());
    }
    changeSkeleton(skeleton) {
        this._skeleton = skeleton;
    }
    leave() {
        this._renderNode._spriteShaderData.removeDefine(SpineShaderInit.SPINE_COLOR2);
    }
    change(currentRender, currentAnimation) {
        this._renderer = currentRender._renderer;
        this._renderNode._spriteShaderData.addDefine(SpineShaderInit.SPINE_COLOR2);
    }
    render(curTime, boneMat) {
        this._renderNode.clear();
        this._renderer.draw(this._skeleton, this._renderNode, -1, -1);
    }
}
class RenderBake {
    get simpleAnimatorTexture() {
        return this._simpleAnimatorTexture;
    }
    set simpleAnimatorTexture(value) {
        if (this._simpleAnimatorTexture) {
            this._simpleAnimatorTexture._removeReference();
        }
        this._simpleAnimatorTexture = value;
        this._simpleAnimatorTextureSize = value.width;
        this._renderNode._spriteShaderData.setTexture(SpineShaderInit.SIMPLE_SIMPLEANIMATORTEXTURE, value);
        value._addReference();
        this._renderNode._spriteShaderData.setNumber(SpineShaderInit.SIMPLE_SIMPLEANIMATORTEXTURESIZE, this._simpleAnimatorTextureSize);
    }
    get simpleAnimatorOffset() {
        return this._simpleAnimatorOffset;
    }
    set simpleAnimatorOffset(value) {
        value.cloneTo(this._simpleAnimatorOffset);
    }
    constructor(renderNode) {
        this.step = 1 / 60;
        this._simpleAnimatorParams = new Vector4();
        this._renderNode = renderNode;
        this._simpleAnimatorOffset = new Vector2();
        this.changeSkeleton(renderNode.getSkeleton());
    }
    changeSkeleton(skeleton) {
        this._skeleton = skeleton;
        this.bones = skeleton.bones;
        this.slots = skeleton.slots;
    }
    leave() {
        this._renderNode._spriteShaderData.removeDefine(SpineShaderInit.SPINE_SIMPLE);
        this._renderNode._renderType = BaseRender2DType.spine;
    }
    change(currentRender, currentAnimation) {
        this.skinRender = currentRender;
        this.currentAnimation = currentAnimation;
        this._renderNode._spriteShaderData.addDefine(SpineShaderInit.SPINE_SIMPLE);
        this._simpleAnimatorOffset.x = this.aniOffsetMap[currentAnimation.name];
        if (currentAnimation.currentSKin.canInstance) {
            this._renderNode._renderType = BaseRender2DType.spineSimple;
        }
    }
    _computeAnimatorParamsData() {
        this._simpleAnimatorParams.x = this._simpleAnimatorOffset.x;
        this._simpleAnimatorParams.y = Math.round(this._simpleAnimatorOffset.y) * this._bonesNums * 2;
    }
    setCustomData(value1, value2 = 0) {
        this._simpleAnimatorParams.z = value1;
        this._simpleAnimatorParams.w = value2;
    }
    render(curTime, boneMat) {
        this.currentAnimation.renderWithOutMat(this.slots, this.skinRender, curTime);
        this._simpleAnimatorOffset.y = curTime / this.step;
        this._computeAnimatorParamsData();
        this._renderNode._spriteShaderData.setVector(SpineShaderInit.SIMPLE_SIMPLEANIMATORPARAMS, this._simpleAnimatorParams);
    }
}
